import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Layout } from '@/components/layout'
import {
  integrationService,
  type EntraTrustedGroupMapping,
  type EntraTrustedGroupMappingInput,
  type EntraTrustedTenant,
  type EntraTrustedTenantInput,
  type IntegrationSettings,
} from '@/lib/services/integrationService'
import { useToast } from '@/lib/hooks/use-toast'
import { getRoles } from '@/lib/services/rbacService'
import type { Role } from '@/lib/types/api'
import {
  Settings,
  CheckCircle,
  XCircle,
  RefreshCw,
  Edit,
  Trash2,
  Power,
  TestTube,
  Plus,
  Shield,
} from 'lucide-react'
import SharePointLogo from '@/assets/sharepoint.svg'
import CoostoLogo from '@/assets/coosto.svg'
import { SharePointConfigDialog } from './components/integration/SharePointConfigDialog'
import { CoostoConfigDialog } from './components/integration/CoostoConfigDialog'
import { EntraTrustedGroupMappingDialog } from './components/integration/EntraTrustedGroupMappingDialog'
import { EntraTrustedTenantDialog } from './components/integration/EntraTrustedTenantDialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export function IntegrationManagementPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [editingIntegration, setEditingIntegration] = useState<string | null>(null)
  const [deletingIntegration, setDeletingIntegration] = useState<string | null>(null)
  const [groupDialogMode, setGroupDialogMode] = useState<'create' | 'edit' | null>(null)
  const [editingGroupMapping, setEditingGroupMapping] = useState<EntraTrustedGroupMapping | null>(null)
  const [deletingGroupMapping, setDeletingGroupMapping] = useState<EntraTrustedGroupMapping | null>(null)
  const [tenantDialogMode, setTenantDialogMode] = useState<'create' | 'edit' | null>(null)
  const [editingTenant, setEditingTenant] = useState<EntraTrustedTenant | null>(null)
  const [deletingTenant, setDeletingTenant] = useState<EntraTrustedTenant | null>(null)

  // Fetch all integrations
  const { data: integrations, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationService.listIntegrations(),
  })

  const { data: trustedGroupMappings } = useQuery({
    queryKey: ['integrations', 'entra', 'group-mappings'],
    queryFn: () => integrationService.listEntraTrustedGroupMappings(),
  })

  const { data: trustedTenants } = useQuery({
    queryKey: ['integrations', 'entra', 'trusted-tenants'],
    queryFn: () => integrationService.listEntraTrustedTenants(),
  })

  const { data: rolesData } = useQuery({
    queryKey: ['rbac', 'roles', 'all'],
    queryFn: () => getRoles(),
  })

  const roles: Role[] = rolesData?.roles ?? []

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['integrations'] })
    queryClient.invalidateQueries({ queryKey: ['integrations', 'entra', 'group-mappings'] })
    queryClient.invalidateQueries({ queryKey: ['integrations', 'entra', 'trusted-tenants'] })
    queryClient.invalidateQueries({ queryKey: ['rbac', 'roles', 'all'] })
  }

  const showMutationError = (description: string) => {
    toast({
      title: t('common.error'),
      description,
      variant: 'destructive',
    })
  }

  // Toggle integration active status
  const toggleMutation = useMutation({
    mutationFn: ({ type, isActive }: { type: string; isActive: boolean }) =>
      integrationService.toggleIntegration(type, isActive),
    onSuccess: () => {
      refreshAll()
      toast({
        title: t('integration.toggled'),
        description: t('integration.toggle_success'),
      })
    },
    onError: () => showMutationError(t('integration.toggle_error')),
  })

  // Test integration
  const testMutation = useMutation({
    mutationFn: (type: string) => integrationService.testIntegration(type),
    onSuccess: (data) => {
      refreshAll()
      toast({
        title: t('integration.test_complete'),
        description: data.message,
        variant: data.data.status === 'success' ? 'default' : 'destructive',
      })
    },
    onError: () => showMutationError(t('integration.test_error')),
  })

  // Delete integration
  const deleteMutation = useMutation({
    mutationFn: (type: string) => integrationService.deleteIntegration(type),
    onSuccess: () => {
      refreshAll()
      setDeletingIntegration(null)
      toast({
        title: t('integration.deleted'),
        description: t('integration.delete_success'),
      })
    },
    onError: () => showMutationError(t('integration.delete_error')),
  })

  const groupMappingMutation = useMutation({
    mutationFn: async ({
      mode,
      id,
      payload,
    }: {
      mode: 'create' | 'edit'
      id?: string
      payload: EntraTrustedGroupMappingInput
    }) => {
      if (mode === 'edit' && id) {
        return integrationService.updateEntraTrustedGroupMapping(id, payload)
      }
      return integrationService.createEntraTrustedGroupMapping(payload)
    },
    onSuccess: () => {
      refreshAll()
      setGroupDialogMode(null)
      setEditingGroupMapping(null)
      toast({
        title: t('integration.entra.groupSaved', 'Groepskoppeling opgeslagen'),
        description: t(
          'integration.entra.groupSavedDescription',
          'Vertrouwde Entra groepskoppeling bijgewerkt.'
        ),
      })
    },
    onError: () => showMutationError(t('integration.entra.groupSaveError', 'Groepskoppeling opslaan mislukt')),
  })

  const groupToggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      integrationService.toggleEntraTrustedGroupMapping(id, isActive),
    onSuccess: () => {
      refreshAll()
      toast({
        title: t('integration.entra.groupToggled', 'Groepskoppeling bijgewerkt'),
        description: t(
          'integration.entra.groupToggledDescription',
          'Status van vertrouwde groepskoppeling is bijgewerkt.'
        ),
      })
    },
    onError: () => showMutationError(t('integration.entra.groupToggleError', 'Groepskoppeling wijzigen mislukt')),
  })

  const groupDeleteMutation = useMutation({
    mutationFn: (id: string) => integrationService.deleteEntraTrustedGroupMapping(id),
    onSuccess: () => {
      refreshAll()
      setDeletingGroupMapping(null)
      toast({
        title: t('integration.entra.groupDeleted', 'Groepskoppeling verwijderd'),
        description: t(
          'integration.entra.groupDeletedDescription',
          'Vertrouwde Entra groepskoppeling is verwijderd.'
        ),
      })
    },
    onError: () => showMutationError(t('integration.entra.groupDeleteError', 'Groepskoppeling verwijderen mislukt')),
  })

  const tenantMutation = useMutation({
    mutationFn: async ({
      mode,
      id,
      payload,
    }: {
      mode: 'create' | 'edit'
      id?: string
      payload: EntraTrustedTenantInput
    }) => {
      if (mode === 'edit' && id) {
        return integrationService.updateEntraTrustedTenant(id, payload)
      }
      return integrationService.createEntraTrustedTenant(payload)
    },
    onSuccess: () => {
      refreshAll()
      setTenantDialogMode(null)
      setEditingTenant(null)
      toast({
        title: t('integration.entra.tenantSaved', 'Tenant opgeslagen'),
        description: t('integration.entra.tenantSavedDescription', 'Vertrouwde tenant bijgewerkt.'),
      })
    },
    onError: () => showMutationError(t('integration.entra.tenantSaveError', 'Tenant opslaan mislukt')),
  })

  const tenantToggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      integrationService.toggleEntraTrustedTenant(id, isActive),
    onSuccess: () => {
      refreshAll()
      toast({
        title: t('integration.entra.tenantToggled', 'Tenant bijgewerkt'),
        description: t('integration.entra.tenantToggledDescription', 'Status van vertrouwde tenant is bijgewerkt.'),
      })
    },
    onError: () => showMutationError(t('integration.entra.tenantToggleError', 'Tenant wijzigen mislukt')),
  })

  const tenantDeleteMutation = useMutation({
    mutationFn: (id: string) => integrationService.deleteEntraTrustedTenant(id),
    onSuccess: () => {
      refreshAll()
      setDeletingTenant(null)
      toast({
        title: t('integration.entra.tenantDeleted', 'Tenant verwijderd'),
        description: t('integration.entra.tenantDeletedDescription', 'Vertrouwde tenant is verwijderd.'),
      })
    },
    onError: () => showMutationError(t('integration.entra.tenantDeleteError', 'Tenant verwijderen mislukt')),
  })

  const handleToggle = (type: string, currentStatus: boolean) => {
    toggleMutation.mutate({ type, isActive: !currentStatus })
  }

  const handleTest = (type: string) => {
    testMutation.mutate(type)
  }

  const handleEdit = (type: string) => {
    setEditingIntegration(type)
  }

  const handleDelete = (type: string) => {
    setDeletingIntegration(type)
  }

  const confirmDelete = () => {
    if (deletingIntegration) {
      deleteMutation.mutate(deletingIntegration)
    }
  }

  const getStatusBadge = (integration: IntegrationSettings) => {
    if (!integration.isConfigured) {
      return <Badge variant="outline">{t('integration.not_configured')}</Badge>
    }
    if (!integration.isActive) {
      return <Badge variant="secondary">{t('integration.inactive')}</Badge>
    }
    return <Badge variant="default" className="bg-green-600 text-white">{t('integration.active')}</Badge>
  }

  const getTestStatusIcon = (status: string | null) => {
    if (!status) return null
    if (status === 'success') {
      return <CheckCircle className="h-4 w-4 text-green-600" />
    }
    return <XCircle className="h-4 w-4 text-red-600" />
  }

  return (
    <Layout>
      {/* Header */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-6 py-6">
          <div className="flex-column md:flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center space-x-2">
                <Settings className="h-8 w-8" />
                <span>{t('integration.title')}</span>
              </h1>
              <p className="text-muted-foreground mt-2">{t('integration.description')}</p>
            </div>
            <Button
              onClick={refreshAll}
              variant="secondary"
              size="sm"
              className="mt-4 md:mt-0"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('common.refresh')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8 space-y-6">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && integrations && integrations.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">{t('integration.no_integrations')}</p>
            </CardContent>
          </Card>
        )}

        {!isLoading && integrations && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* SharePoint Integration */}
              {!integrations.find(i => i.integrationType === 'sharepoint') && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <img src={SharePointLogo} alt="SharePoint" className="h-8 w-8" />
                      <CardTitle>SharePoint</CardTitle>
                    </div>
                    <CardDescription>Microsoft SharePoint document integration</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="gradient" onClick={() => handleEdit('sharepoint')} className="w-full">
                      <Settings className="h-4 w-4 mr-2" />
                      {t('integration.configure')}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {integrations
                .filter(i => i.integrationType === 'sharepoint')
                .map(integration => (
                  <Card key={integration.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <img src={SharePointLogo} alt="SharePoint" className="h-8 w-8 mt-1 flex-shrink-0" />
                          <div>
                            <CardTitle className="flex items-center space-x-2">
                              <span>{integration.name}</span>
                              {getStatusBadge(integration)}
                            </CardTitle>
                            <CardDescription className="mt-2">
                              {integration.description}
                            </CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {integration.lastTestedAt && (
                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                          {getTestStatusIcon(integration.lastTestStatus)}
                          <span>
                            {t('integration.last_tested')}:{' '}
                            {new Date(integration.lastTestedAt).toLocaleString()}
                          </span>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2">
                        <Button onClick={() => handleEdit(integration.integrationType)} variant="outline" size="sm">
                          <Edit className="h-4 w-4 mr-2" />
                          {t('common.edit')}
                        </Button>
                        <Button
                          onClick={() => handleToggle(integration.integrationType, integration.isActive)}
                          variant="outline"
                          size="sm"
                        >
                          <Power className="h-4 w-4 mr-2" />
                          {integration.isActive ? t('common.disable') : t('common.enable')}
                        </Button>
                        <Button
                          onClick={() => handleTest(integration.integrationType)}
                          variant="outline"
                          size="sm"
                          disabled={testMutation.isPending}
                        >
                          <TestTube className="h-4 w-4 mr-2" />
                          {t('integration.test')}
                        </Button>
                        <Button
                          onClick={() => handleDelete(integration.integrationType)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('common.delete')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}

              {/* Coosto Integration */}
              {!integrations.find(i => i.integrationType === 'coosto') && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <img src={CoostoLogo} alt="Coosto" className="h-8 w-8" />
                      <CardTitle>Coosto</CardTitle>
                    </div>
                    <CardDescription>Coosto media monitoring integration</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="gradient" onClick={() => handleEdit('coosto')} className="w-full">
                      <Settings className="h-4 w-4 mr-2" />
                      {t('integration.configure')}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {integrations
                .filter(i => i.integrationType === 'coosto')
                .map(integration => (
                  <Card key={integration.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <img src={CoostoLogo} alt="Coosto" className="h-8 w-8 mt-1 flex-shrink-0" />
                          <div>
                            <CardTitle className="flex items-center space-x-2">
                              <span>{integration.name}</span>
                              {getStatusBadge(integration)}
                            </CardTitle>
                            <CardDescription className="mt-2">
                              {integration.description}
                            </CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {integration.lastTestedAt && (
                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                          {getTestStatusIcon(integration.lastTestStatus)}
                          <span>
                            {t('integration.last_tested')}:{' '}
                            {new Date(integration.lastTestedAt).toLocaleString()}
                          </span>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2">
                        <Button onClick={() => handleEdit(integration.integrationType)} variant="outline" size="sm">
                          <Edit className="h-4 w-4 mr-2" />
                          {t('common.edit')}
                        </Button>
                        <Button
                          onClick={() => handleToggle(integration.integrationType, integration.isActive)}
                          variant="outline"
                          size="sm"
                        >
                          <Power className="h-4 w-4 mr-2" />
                          {integration.isActive ? t('common.disable') : t('common.enable')}
                        </Button>
                        <Button
                          onClick={() => handleTest(integration.integrationType)}
                          variant="outline"
                          size="sm"
                          disabled={testMutation.isPending}
                        >
                          <TestTube className="h-4 w-4 mr-2" />
                          {t('integration.test')}
                        </Button>
                        <Button
                          onClick={() => handleDelete(integration.integrationType)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('common.delete')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>

            <Card>
              <CardHeader className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    {t('integration.entra.trustedGroupsTitle', 'Vertrouwde Entra groep-mappings')}
                  </CardTitle>
                  <CardDescription>
                    {t(
                      'integration.entra.trustedGroupsDescription',
                      'Niet-gewhiteliste tenants krijgen alleen toegang via ingeschakelde group Object ID mappings.'
                    )}
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    setEditingGroupMapping(null)
                    setGroupDialogMode('create')
                  }}
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('integration.entra.addTrustedGroup', 'Groep toevoegen')}
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {trustedGroupMappings?.length ? (
                  trustedGroupMappings.map(mapping => (
                    <div
                      key={mapping.id}
                      className="rounded-lg border bg-background/60 p-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
                    >
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{mapping.name}</span>
                          <Badge variant={mapping.isActive ? 'default' : 'secondary'}>
                            {mapping.isActive ? t('integration.active') : t('integration.inactive')}
                          </Badge>
                          <Badge variant="outline">{mapping.roleSlug}</Badge>
                        </div>
                        <p className="font-mono text-xs break-all text-muted-foreground">
                          {mapping.groupObjectId}
                        </p>
                        {mapping.description ? (
                          <p className="text-sm text-muted-foreground">{mapping.description}</p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingGroupMapping(mapping)
                            setGroupDialogMode('edit')
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            groupToggleMutation.mutate({
                              id: mapping.id,
                              isActive: !mapping.isActive,
                            })
                          }
                        >
                          <Power className="h-4 w-4 mr-2" />
                          {mapping.isActive ? t('common.disable') : t('common.enable')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setDeletingGroupMapping(mapping)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('common.delete')}
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('integration.entra.noTrustedGroups', 'Nog geen vertrouwde group mappings geconfigureerd.')}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    {t('integration.entra.trustedTenantsTitle', 'Tenant whitelist voor roles-claims')}
                  </CardTitle>
                  <CardDescription>
                    {t(
                      'integration.entra.trustedTenantsDescription',
                      'Alleen deze tenants mogen roles-claims blijven gebruiken voor autorisatie.'
                    )}
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    setEditingTenant(null)
                    setTenantDialogMode('create')
                  }}
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('integration.entra.addTrustedTenant', 'Tenant toevoegen')}
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {trustedTenants?.length ? (
                  trustedTenants.map(tenant => (
                    <div
                      key={tenant.id}
                      className="rounded-lg border bg-background/60 p-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
                    >
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{tenant.name}</span>
                          <Badge variant={tenant.isActive ? 'default' : 'secondary'}>
                            {tenant.isActive ? t('integration.active') : t('integration.inactive')}
                          </Badge>
                          <Badge variant={tenant.allowRoleClaims ? 'outline' : 'secondary'}>
                            {tenant.allowRoleClaims
                              ? t('integration.entra.allowRoleClaims', 'Roles-claims toegestaan')
                              : t('integration.entra.allowRoleClaimsDisabled', 'Roles-claims geblokkeerd')}
                          </Badge>
                        </div>
                        <p className="font-mono text-xs break-all text-muted-foreground">
                          {tenant.tenantId}
                        </p>
                        {tenant.description ? (
                          <p className="text-sm text-muted-foreground">{tenant.description}</p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingTenant(tenant)
                            setTenantDialogMode('edit')
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            tenantToggleMutation.mutate({
                              id: tenant.id,
                              isActive: !tenant.isActive,
                            })
                          }
                        >
                          <Power className="h-4 w-4 mr-2" />
                          {tenant.isActive ? t('common.disable') : t('common.enable')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setDeletingTenant(tenant)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('common.delete')}
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('integration.entra.noTrustedTenants', 'Nog geen tenants op whitelist voor roles-claims.')}
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Configuration Dialogs */}
      {editingIntegration === 'sharepoint' && (
        <SharePointConfigDialog
          open={true}
          onClose={() => setEditingIntegration(null)}
          onSuccess={() => {
            setEditingIntegration(null)
            queryClient.invalidateQueries({ queryKey: ['integrations'] })
          }}
        />
      )}

      {editingIntegration === 'coosto' && (
        <CoostoConfigDialog
          open={true}
          onClose={() => setEditingIntegration(null)}
          onSuccess={() => {
            setEditingIntegration(null)
            queryClient.invalidateQueries({ queryKey: ['integrations'] })
          }}
        />
      )}

      <EntraTrustedGroupMappingDialog
        open={groupDialogMode !== null}
        mode={groupDialogMode ?? 'create'}
        mapping={editingGroupMapping}
        roles={roles}
        onOpenChange={(open) => {
          if (!open) {
            setGroupDialogMode(null)
            setEditingGroupMapping(null)
          }
        }}
        onSubmit={async payload => {
          await groupMappingMutation.mutateAsync(
            editingGroupMapping?.id
              ? {
                  mode: groupDialogMode ?? 'create',
                  id: editingGroupMapping.id,
                  payload,
                }
              : {
                  mode: groupDialogMode ?? 'create',
                  payload,
                }
          )
        }}
      />

      <EntraTrustedTenantDialog
        open={tenantDialogMode !== null}
        mode={tenantDialogMode ?? 'create'}
        tenant={editingTenant}
        onOpenChange={(open) => {
          if (!open) {
            setTenantDialogMode(null)
            setEditingTenant(null)
          }
        }}
        onSubmit={async payload => {
          await tenantMutation.mutateAsync(
            editingTenant?.id
              ? {
                  mode: tenantDialogMode ?? 'create',
                  id: editingTenant.id,
                  payload,
                }
              : {
                  mode: tenantDialogMode ?? 'create',
                  payload,
                }
          )
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deletingIntegration !== null} onOpenChange={() => setDeletingIntegration(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('integration.delete_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('integration.delete_confirm_message')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deletingGroupMapping !== null} onOpenChange={() => setDeletingGroupMapping(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('integration.entra.deleteGroupTitle', 'Groepskoppeling verwijderen')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'integration.entra.deleteGroupDescription',
                'Weet u zeker dat u deze vertrouwde group mapping wilt verwijderen?'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingGroupMapping && groupDeleteMutation.mutate(deletingGroupMapping.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deletingTenant !== null} onOpenChange={() => setDeletingTenant(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('integration.entra.deleteTenantTitle', 'Tenant verwijderen')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'integration.entra.deleteTenantDescription',
                'Weet u zeker dat u deze tenant uit de roles-claim whitelist wilt verwijderen?'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingTenant && tenantDeleteMutation.mutate(deletingTenant.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  )
}
